// @ts-strict-ignore
import { UrlSegment, type Route, type UrlMatchResult, type UrlSegmentGroup } from '@angular/router';

import { logger } from 'src/app/core/services/logger/logger';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { AppRouteQueryParam } from 'src/app/routing/app-route-query-params';
import type { Subprocess } from 'src/app/utils/models/subprocess.enum';

export interface MarketContext {
  subprocess: Subprocess;
  country: string;
  language: string;
}

export interface RccUrlData {
  context: MarketContext;
  page: string;
  queryParams: Record<string, string>;
  configurationStep?: string;
  onlineCode?: string;
  baumuster?: number;
  nst?: string;
  vehicleId?: string;
  vehicleBody?: string;
  vehicleClass?: string;
}

export const validRccQueryParams = [
  AppRouteQueryParam.GsOutletId,
  AppRouteQueryParam.CompanyId,
  AppRouteQueryParam.DealerGroup,
  AppRouteQueryParam.AllGsOutletIds
];

export const rccPages = {
  motorization: AppRoutePath.Motorization,
  carconfig: AppRoutePath.Configuration,
  summary: AppRoutePath.Configuration
};

// e.g. "https://configurator.mercedes-benz.com/configurator/tw/en/..." or "https://configurator.mercedes-benz.com/configurador/es/es/..."
const retailUrlPattern = /configurator|konfigurator|configurador|configurateur/;

// leading slash is important to not have a conflict with car-configurator.html
const retailUrlPatternWithSlashes = /\/configurator|\/konfigurator|\/configurador|\/configurateur/;

const wholesaleUrlPattern = /(passengercars)|(mercedes-benz-cars)|(car-configurator)/;

/**
 * Returns an object if processed URL (segments) indicate that this is a RCC URL pattern, null otherwise.
 * Example entry links with RCC pattern (PROD):
 * - retail URL: https://configurator.mercedes-benz.com/configurator/de/de/model/body
 *  ?model=1770821
 *  &nst=
 *  &subprocess=CCri
 *  &companyId=GC0009056&gsOutletId=GS0010844
 *  &allGsOutletIds=GS0010844,GS0000897,GS0000899,GS0000898,GS0000900,GS0000901,GS0001173,GS0005185,GS0025995,GS0001177,GS0000563,GS0090567,GS0078183
 *  &dealerGroup=false
 * - wholesale URL: https://www.mercedes-benz.de/passengercars/content-pool/tool-pages/car-configurator.html/
 *  ?rccVehicleClassId=A-KLASSE&rccVehicleBodyId=KOMPAKT-LIMOUSINE
 */
export function isRccUrlPatternInStandaloneExecutionMode(
  urlSegments: UrlSegment[],
  group: UrlSegmentGroup, // required for UrlMatcher
  route: Route, // required for UrlMatcher
  // eslint-disable-next-line no-restricted-globals
  win: Window = window
): UrlMatchResult {
  const isRccRetailUrlPattern = (segments: UrlSegment[]): boolean => {
    logger.log('isRccRetailUrlPattern', segments, retailUrlPattern.test(segments[0].path));
    return retailUrlPattern.test(segments[0].path);
  };

  const isRccWholesaleUrlPattern = (segments: UrlSegment[]): boolean => {
    let hasRccWholesaleUrlPatternIndicator = false;
    let urlSegment: UrlSegment;

    while (!hasRccWholesaleUrlPatternIndicator && segments.length > 0) {
      urlSegment = segments.pop();
      hasRccWholesaleUrlPatternIndicator = wholesaleUrlPattern.test(urlSegment.path);
    }

    logger.log('isRccWholesaleUrlPattern', segments, hasRccWholesaleUrlPatternIndicator);
    return hasRccWholesaleUrlPatternIndicator;
  };

  if (!hasQueryParams(win)) {
    // no query params => no RCC URL pattern
    return null;
  }

  if (urlSegments.length === 0) {
    return null;
  }

  if (urlSegments.some(item => item.path === 'error')) {
    return null;
  }

  if (isRccRetailUrlPattern(urlSegments)) {
    return { consumed: urlSegments };
  }

  if (isRccWholesaleUrlPattern(urlSegments)) {
    // Note: SOON NOT NECESSARY ANYMORE, BUT NEEDED FOR MIGRATION PURPOSES
    return { consumed: adaptedUrlSegments(win) };
  }

  return null;
}

export function isRccUrlPatternInIntegratedExecutionMode(
  urlSegments: UrlSegment[],
  group: UrlSegmentGroup, // required for UrlMatcher
  route: Route, // required for UrlMatcher
  // eslint-disable-next-line no-restricted-globals
  win: Window = window
): UrlMatchResult {
  const isRccRetailUrlPattern = (windowLocationPathName: string): boolean => {
    logger.log(
      'isRccRetailUrlPattern',
      windowLocationPathName,
      retailUrlPatternWithSlashes.test(windowLocationPathName)
    );
    return retailUrlPatternWithSlashes.test(windowLocationPathName);
  };

  const isRccWholesaleUrlPattern = (windowLocationPathName: string): boolean => {
    logger.log('isRccWholesaleUrlPattern', windowLocationPathName, wholesaleUrlPattern.test(windowLocationPathName));
    return wholesaleUrlPattern.test(windowLocationPathName);
  };

  if (!hasQueryParams(win)) {
    // no query params => no RCC URL pattern
    return null;
  }

  if (urlSegments.some(item => item.path === 'error')) {
    return null;
  }

  if (urlSegments.length === 0) {
    // making sure it is _not_ the standalone mode
    // if OWCC is running in OneWeb integrated mode
    if (isRccRetailUrlPattern(win.location.pathname)) {
      // Note: This actually should never happen since OWCC retails will always be standalone, never OneWeb integrated
      return { consumed: [] };
    }
    if (isRccWholesaleUrlPattern(win.location.pathname)) {
      return { consumed: adaptedUrlSegments(win) };
    }
  }

  return null;
}

export function hasQueryParams(win: Window): boolean {
  return !!win.location?.search;
}

// eslint-disable-next-line no-restricted-globals
export function adaptedUrlSegments(win: Window = window): UrlSegment[] {
  // Note: DE/de is the default country and locale.
  // Why? Because it was like this before (and with our integration plan it should not cause issues).
  const countryFallback = 'DE';
  const languageFallback = 'de';

  // Country and Language is fetch from a global window object. Nicer would be the store, but the injector is not available in all the cases!
  win.owcc = win.owcc || {};
  const country = win.owcc.country || countryFallback;
  const language = win.owcc.language || languageFallback;

  const urlBasePattern = `configurator/${country}/${language}/class/body`;
  const newUrlSegments: UrlSegment[] = [];

  for (const segment of urlBasePattern.split('/')) {
    newUrlSegments.push(new UrlSegment(segment, {}));
  }

  return newUrlSegments;
}
